import { Easing, interpolate, useCurrentFrame } from 'remotion';

// Utils
import { buildStarPoints } from '../../utils';

// Constants
import { CLAMP_BOTH, CLAMP_RIGHT } from '@/constants/animations';
import {
  PHOTO_FLASH_ANIMATION,
  CAMERA_SIZE,
  FLOWER_SIZE,
  FLASH_COLOR,
  FLASH_RAYS,
} from '../../constants';

// Icons
import { CameraClassicIcon } from '@shared/icons/CameraClassicIcon';
import { FlowerIcon } from '@shared/icons/FlowerIcon';

// Pre-computed star polygon: 12 tips with varied outer radii, uniform inner radius
const STAR_POINTS = buildStarPoints(
  [28, 20, 26, 18, 30, 22, 27, 19, 24, 21, 29, 17],
  8,
);

export const PhotoFlashAnimation = () => {
  const frame = useCurrentFrame();

  // Camera drops in from above after wave slide-in completes
  const cameraOpacity = interpolate(
    frame,
    [
      PHOTO_FLASH_ANIMATION.cameraDropStart - 1,
      PHOTO_FLASH_ANIMATION.cameraDropStart,
    ],
    [0, 1],
    CLAMP_BOTH,
  );
  const cameraY = interpolate(
    frame,
    [
      PHOTO_FLASH_ANIMATION.cameraDropStart,
      PHOTO_FLASH_ANIMATION.cameraDropEnd,
    ],
    [-PHOTO_FLASH_ANIMATION.cameraDropHeight, 0],
    {
      ...CLAMP_BOTH,
      easing: Easing.out(Easing.cubic),
    },
  );

  // Shared flash progress drives both star glow and rays
  const flashProgress = interpolate(
    frame,
    [PHOTO_FLASH_ANIMATION.flashStart, PHOTO_FLASH_ANIMATION.flashEnd],
    [0, 1],
    CLAMP_BOTH,
  );
  const glowScale = interpolate(flashProgress, [0, 0.25, 1], [0.25, 1.75, 2.5]);
  const glowOpacity = interpolate(
    flashProgress,
    [0, 0.25, 0.5, 1],
    [0, 1, 0.75, 0],
    CLAMP_BOTH,
  );
  const rayOpacity = interpolate(
    flashProgress,
    [0, 0.25, 0.5, 1],
    [0, 1, 0.75, 0],
    CLAMP_BOTH,
  );

  // Parabolic arc sideways: -4h·t·(1-t) for peak, +yOffset for final position
  const jumpProgress = interpolate(
    frame,
    [
      PHOTO_FLASH_ANIMATION.flowerJumpStart,
      PHOTO_FLASH_ANIMATION.flowerJumpEnd,
    ],
    [0, 1],
    CLAMP_BOTH,
  );
  const flowerX = -PHOTO_FLASH_ANIMATION.jumpDistance * jumpProgress;
  const flowerY =
    -4 *
      PHOTO_FLASH_ANIMATION.jumpArcHeight *
      jumpProgress *
      (1 - jumpProgress) +
    jumpProgress * PHOTO_FLASH_ANIMATION.jumpYOffset;

  // Decaying bounce on landing
  const bounceFrame = Math.max(0, frame - PHOTO_FLASH_ANIMATION.flowerJumpEnd);
  const landingBounce =
    Math.exp(-bounceFrame * 0.25) * Math.sin(bounceFrame * 0.5) * 12;

  const flowerOpacity = interpolate(
    frame,
    [
      PHOTO_FLASH_ANIMATION.flowerJumpStart,
      PHOTO_FLASH_ANIMATION.flowerJumpStart + 10,
    ],
    [0, 1],
    CLAMP_RIGHT,
  );
  const flowerRotation =
    (Math.max(0, frame - PHOTO_FLASH_ANIMATION.flowerSpinStart) /
      PHOTO_FLASH_ANIMATION.spinPeriod) *
    360;

  return (
    <div className="absolute inset-0 pointer-events-none">
      {/* Camera drops in, flash bursts on landing */}
      <div
        className="absolute"
        style={{
          left: PHOTO_FLASH_ANIMATION.cameraLeft,
          top: PHOTO_FLASH_ANIMATION.cameraTop,
          opacity: cameraOpacity,
          transform: `translate(-50%, calc(-50% + ${cameraY}px))`,
          width: CAMERA_SIZE,
        }}
      >
        <CameraClassicIcon width={CAMERA_SIZE} height="auto" />

        {/* Star-shaped center glow */}
        <svg
          className="absolute top-1/2 left-1/2 overflow-visible"
          style={{
            transform: `translate(-50%, -50%) scale(${glowScale})`,
            opacity: glowOpacity,
          }}
          width={70}
          height={70}
          viewBox="-35 -35 70 70"
        >
          <polygon points={STAR_POINTS} fill={FLASH_COLOR} />
        </svg>

        {/* Flash rays */}
        {FLASH_RAYS.map(({ angle, length, width }, i) => {
          const rayLength = interpolate(
            flashProgress,
            [0, 0.25, 0.75, 1],
            [0, length * 0.5, length, length * 1.25],
          );
          return (
            <div
              key={i}
              className="absolute top-1/2 left-1/2 rounded-full"
              style={{
                width: rayLength,
                height: width,
                background: `linear-gradient(to right, ${FLASH_COLOR}, rgba(255,255,255,0.3), transparent)`,
                opacity: rayOpacity,
                transformOrigin: '0 50%',
                transform: `rotate(${angle}deg)`,
              }}
            />
          );
        })}
      </div>

      {/* Flower jumps left then spins */}
      <div
        className="absolute"
        style={{
          left: PHOTO_FLASH_ANIMATION.cameraLeft,
          top: PHOTO_FLASH_ANIMATION.cameraTop,
          opacity: flowerOpacity,
          transform: `translate(calc(-50% + ${flowerX}px), calc(-50% + ${flowerY + landingBounce}px)) rotate(${flowerRotation}deg)`,
          width: FLOWER_SIZE,
        }}
      >
        <FlowerIcon width={FLOWER_SIZE} height={FLOWER_SIZE} />
      </div>
    </div>
  );
};
